import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  IndentIncrease,
  Italic,
  LetterText,
  Strikethrough,
  Underline,
  X,
} from "lucide-react";

const fontFamilies = [
  "Arial, sans-serif",
  "'Courier New', monospace",
  "'Times New Roman', serif",
  "'Roboto', sans-serif",
  "'Georgia', serif",
];

const ShortTextboxToolbar = ({
  questionId,
  isLeftSidebarOpen,
  leftSidebarWidth = 0,
  isRightSidebarOpen,
  windowWidth,
  selectedComponent,
  onComponentUpdate,
  compType,
  onDeselect,
}) => {
  const [isMobile, setIsMobile] = useState(windowWidth < 768);
  const [fontSizeInput, setFontSizeInput] = useState(
    String(selectedComponent?.fontSize ?? 14)
  );
  const [lineSpacingInput, setLineSpacingInput] = useState(
    String(selectedComponent?.lineSpacing ?? 1.2)
  );
  const [letterSpacingInput, setLetterSpacingInput] = useState(
    String(selectedComponent?.letterSpacing ?? 0)
  );

  useEffect(() => {
    setIsMobile(windowWidth < 768);
  }, [windowWidth]);

  useEffect(() => {
    setFontSizeInput(String(selectedComponent?.fontSize ?? 14));
  }, [selectedComponent?.fontSize]);

  useEffect(() => {
    setLineSpacingInput(String(selectedComponent?.lineSpacing ?? 1.2));
  }, [selectedComponent?.lineSpacing]);

  useEffect(() => {
    setLetterSpacingInput(String(selectedComponent?.letterSpacing ?? 0));
  }, [selectedComponent?.letterSpacing]);

  const calculatePosition = () => {
    if (windowWidth < 768) {
      return { left: "50%", transform: "translateX(-50%)" };
    }
    const leftSidebarOffset = isLeftSidebarOpen ? leftSidebarWidth / 2 : 0;
    const rightSidebarOffset = isRightSidebarOpen ? -32 : 0;
    const totalOffset = leftSidebarOffset + rightSidebarOffset;
    const leftOffset =
      totalOffset !== 0 ? `calc(50% + ${totalOffset}px)` : "50%";
    return { left: leftOffset, transform: "translateX(-50%)" };
  };

  const position = calculatePosition();

  // Helpers for numeric inputs
  const clampNumber = (value, min, max, fallback) => {
    const parsed = parseInt(String(value), 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
  };

  const clampFloat = (value, min, max, fallback, decimals = 1) => {
    const parsed = parseFloat(String(value));
    if (!Number.isFinite(parsed)) return fallback;
    const clamped = Math.min(max, Math.max(min, parsed));
    const factor = Math.pow(10, decimals);
    return Math.round(clamped * factor) / factor;
  };

  const commitFontSize = () => {
    if (!selectedComponent?.id) return;
    const clamped = clampNumber(fontSizeInput, 8, 72, 14);
    setFontSizeInput(String(clamped));
    if (selectedComponent?.fontSize !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        fontSize: clamped,
      });
    }
  };

  const resetFontSizeInput = () => {
    setFontSizeInput(String(selectedComponent?.fontSize ?? 14));
  };

  const commitLineSpacing = () => {
    if (!selectedComponent?.id) return;
    const clamped = clampFloat(lineSpacingInput, 1, 3, 1.2, 1);
    setLineSpacingInput(String(clamped.toFixed(1)));
    if (selectedComponent?.lineSpacing !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        lineSpacing: clamped,
      });
    }
  };

  const resetLineSpacingInput = () => {
    setLineSpacingInput(String(selectedComponent?.lineSpacing ?? 1.2));
  };

  const commitLetterSpacing = () => {
    if (!selectedComponent?.id) return;
    const clamped = clampFloat(letterSpacingInput, 0, 10, 0, 1);
    setLetterSpacingInput(String(clamped.toFixed(1)));
    if (selectedComponent?.letterSpacing !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        letterSpacing: clamped,
      });
    }
  };

  const resetLetterSpacingInput = () => {
    setLetterSpacingInput(String(selectedComponent?.letterSpacing ?? 0));
  };

  return (
    <div
      className="fixed z-50 transition-all duration-300"
      style={{
        left: position.left,
        transform: position.transform,
        bottom: isMobile ? "calc(34px + env(safe-area-inset-bottom, 0px))" : 34,
      }}
    >
      <motion.div
        key={questionId}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className={`bg-white rounded-xl shadow-xl border border-gray-200 relative`}
        style={{
          width: isMobile ? "calc(100vw - 24px)" : "600px",
          maxWidth: "calc(100vw - 24px)",
          maxHeight: isMobile ? "40vh" : "none",
          overflowY: isMobile ? "auto" : "visible",
        }}
      >
        <button
          onClick={onDeselect}
          className={`${
            isMobile ? "top-3 right-2" : "-top-3 right-2"
          } absolute bg-white rounded-full ${
            isMobile ? "p-1" : "p-1.5"
          } shadow-md border border-gray-200`}
          aria-label="Close"
          title="Close"
        >
          <X size={isMobile ? 14 : 16} className="text-gray-700" />
        </button>

        <div className={`${isMobile ? "px-2 py-3" : "px-4 py-4"} w-full`}>
          <div className="flex flex-col gap-3">
            {/* Row 1: Typography controls in responsive grid */}
            <div
              className="grid gap-2"
              style={{
                gridTemplateColumns: isMobile
                  ? "repeat(auto-fit, minmax(120px, 1fr))"
                  : "repeat(3, minmax(0, 1fr))",
              }}
            >
              {/* Font Family & Size */}
              <div className="flex flex-col gap-2">
                <div className="flex gap-1">
                  <label className="flex-1">
                    <select
                      value={selectedComponent?.fontFamily || fontFamilies[0]}
                      onChange={(e) =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          fontFamily: e.target.value,
                        })
                      }
                      className="block w-full rounded-md border border-gray-300 p-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      {fontFamilies.map((f) => (
                        <option key={f} value={f}>
                          {f.split(",")[0]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="w-12 md:w-10">
                    <input
                      type="number"
                      min={8}
                      max={72}
                      value={fontSizeInput}
                      onChange={(e) => {
                        const next = e.target.value;
                        setFontSizeInput(next);
                        const parsed = parseInt(next, 10);
                        if (
                          Number.isFinite(parsed) &&
                          parsed >= 8 &&
                          parsed <= 72 &&
                          selectedComponent?.fontSize !== parsed
                        ) {
                          onComponentUpdate(questionId, selectedComponent.id, {
                            fontSize: parsed,
                          });
                        }
                      }}
                      onBlur={commitFontSize}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitFontSize();
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          resetFontSizeInput();
                        }
                      }}
                      className="block w-full rounded-md border border-gray-300 p-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </label>
                </div>
                {/* Font Style Toggles */}
                <div className="flex flex-wrap gap-1">
                  {["bold", "italic", "underline", "lineThrough"].map(
                    (style) => {
                      const iconMap = {
                        bold: <Bold className="h-5 w-5" />,
                        italic: <Italic className="h-5 w-5" />,
                        underline: <Underline className="h-5 w-5" />,
                        lineThrough: <Strikethrough className="h-5 w-5" />,
                      };
                      const titleMap = {
                        bold: "Bold",
                        italic: "Italic",
                        underline: "Underline",
                        lineThrough: "Strikethrough",
                      };
                      const isActive = Boolean(
                        selectedComponent?.fontStyles?.[style]
                      );
                      return (
                        <button
                          key={style}
                          onClick={() =>
                            onComponentUpdate(
                              questionId,
                              selectedComponent.id,
                              {
                                fontStyles: {
                                  ...selectedComponent.fontStyles,
                                  [style]:
                                    !selectedComponent.fontStyles?.[style],
                                },
                              }
                            )
                          }
                          className={`w-8 h-8 rounded border text-sm font-semibold flex items-center justify-center select-none
                      ${
                        isActive
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                      }`}
                          title={titleMap[style]}
                          aria-pressed={isActive}
                          type="button"
                        >
                          {iconMap[style]}
                        </button>
                      );
                    }
                  )}
                </div>
              </div>

              {/* Spacing controls */}
              <div className="flex flex-col gap-2">
                {/* Line Spacing */}
                <label>
                  <p className="text-xs text-black font-semibold mb-1">
                    Line Spacing
                  </p>
                  <div className="flex items-center gap-2 px-2 w-full h-8 overflow-hidden rounded-md bg-gray-300">
                    <LetterText className="h-5 w-5" />
                    <input
                      type="number"
                      min={1}
                      max={3}
                      step={0.1}
                      value={lineSpacingInput}
                      onChange={(e) => {
                        const next = e.target.value;
                        setLineSpacingInput(next);
                        const parsed = parseFloat(next);
                        if (
                          Number.isFinite(parsed) &&
                          parsed >= 1 &&
                          parsed <= 3 &&
                          selectedComponent?.lineSpacing !== parsed
                        ) {
                          onComponentUpdate(questionId, selectedComponent.id, {
                            lineSpacing: parsed,
                          });
                        }
                      }}
                      onBlur={commitLineSpacing}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitLineSpacing();
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          resetLineSpacingInput();
                        }
                      }}
                      className="w-10 border-none text-[11px] font-medium bg-transparent focus:outline-none"
                    />
                  </div>
                </label>
                {/* Letter Spacing */}
                <label>
                  <p className="text-xs text-black font-semibold mb-1">
                    Letter Spacing
                  </p>
                  <div className="flex items-center gap-2 px-2 w-full h-8 overflow-hidden rounded-md bg-gray-300">
                    <IndentIncrease className="h-5 w-5" />
                    <input
                      type="number"
                      min={0}
                      max={10}
                      step={0.1}
                      value={letterSpacingInput}
                      onChange={(e) => {
                        const next = e.target.value;
                        setLetterSpacingInput(next);
                        const parsed = parseFloat(next);
                        if (
                          Number.isFinite(parsed) &&
                          parsed >= 0 &&
                          parsed <= 10 &&
                          selectedComponent?.letterSpacing !== parsed
                        ) {
                          onComponentUpdate(questionId, selectedComponent.id, {
                            letterSpacing: parsed,
                          });
                        }
                      }}
                      onBlur={commitLetterSpacing}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitLetterSpacing();
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          resetLetterSpacingInput();
                        }
                      }}
                      className="w-10 border-none text-[11px] font-medium bg-transparent focus:outline-none"
                    />
                  </div>
                </label>
              </div>

              {/* Alignment and font color */}
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 flex-wrap">
                  {[
                    { align: "left", icon: <AlignLeft className="h-6 w-6" /> },
                    {
                      align: "center",
                      icon: <AlignCenter className="h-6 w-6" />,
                    },
                    {
                      align: "right",
                      icon: <AlignRight className="h-6 w-6" />,
                    },
                  ].map(({ align, icon }) => (
                    <button
                      key={align}
                      onClick={() =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          textAlign: align,
                        })
                      }
                      className={`w-8 h-8 rounded border border-transparent flex items-center justify-center select-none
                           ${
                             selectedComponent?.textAlign === align
                               ? "bg-gray-400 text-white border-gray-400"
                               : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                           }`}
                      type="button"
                    >
                      {icon}
                    </button>
                  ))}
                </div>
                <label className="flex-1">
                  <div
                    className={`rounded-xl shadow-md shadow-black/40 flex justify-center items-center overflow-hidden border-2 border-white ${
                      isMobile ? "w-12 h-12" : "w-16 h-16"
                    }`}
                  >
                    <input
                      type="color"
                      value={selectedComponent?.fontColor || "#000000"}
                      onChange={(e) =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          fontColor: e.target.value,
                        })
                      }
                      className={`${
                        isMobile
                          ? "w-[54px] h-[54px] min-w-[54px] min-h-[54px] max-w-[54px] max-h-[54px]"
                          : "w-[70px] h-[70px] min-w-[70px] min-h-[70px] max-w-[70px] max-h-[70px]"
                      } -m-1 border-none p-0 focus:outline-none ring-0 outline-none`}
                    />
                  </div>
                </label>
              </div>
            </div>

            {/* Optional lists and align controls remain as-is below */}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ShortTextboxToolbar;
